<template>
  <app-card
    :border-color="cardBorderColor"
    body-class="space-y-6"
  >
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <app-icon
          name="bank-note"
          variant="solid"
        />
        <p class="text-base font-semibold">{{ transfer.public_id }}</p>
      </div>
      <transfer-status-badge :status="transfer.status || TransferStatus.CREATED" />
    </div>

    <div class="space-y-3">
      <data-property
        icon="building-eight"
        direction="horizontal"
        :label="senderBeneficiaryLabel"
        :is-loading="isLoading"
        data-sentry-unmask
      >
        <template #value>
          <text-truncate
            :value="isAgencyUser ? senderBusinessName : recipientBusinessName"
            :max-length="28"
          />
        </template>
      </data-property>
      <data-property
        icon="coin-hand"
        direction="horizontal"
        label="Commissionable fare"
        :value="totalCommission"
        :is-loading="isLoading"
      />
    </div>

    <transfer-timeline
      :transfer="transfer"
      :is-loading="isLoading"
    />

    <transfer-recipient-invoice-provider-alert
      v-if="invoicingDisabled"
      :invoice-provider="invoiceProvider"
      :is-agency="isAgencyUser"
    />

    <slot name="actions">
      <transfer-actions
        v-if="transfer.id"
        :transfer-id="transfer.id"
        :status="transfer.status"
      >
        <button
          class="btn btn-secondary"
          :onClick="handleSeeDetails"
        >
          <app-icon
            name="link-external"
            size="sm"
          />
          See details
        </button>
      </transfer-actions>
    </slot>
  </app-card>

  <transfer-details-status-info
    :transfer="transfer"
    v-if="[TransferStatus.FAILED_VERIFICATION, TransferStatus.CANCELED].includes(transfer?.status)"
  />
</template>

<script setup>
import { computed } from 'vue';
import { formatNumber } from '@/utils/number';
import { TransferStatus } from '@/constants/transfers';
import { InvoiceProviders } from '@/constants/transfer-recipients';
import { useAuthStore } from '@/stores/auth';
import { useRouter } from 'vue-router';
import { useTransferDrawerStore } from '@/stores/transfers';
import TransferRecipientInvoiceProviderAlert from '@/components/transfer-recipients/TransferRecipientInvoiceProviderAlert.vue';
import TransferStatusBadge from '@/components/transfers/TransferStatusBadge.vue';
import TransferTimeline from '@/components/transfers/TransferTimeline.vue';
import TransferActions from '@/components/transfers/TransferActions.vue';
import TransferDetailsStatusInfo from '@/components/transfers/TransferDetails/TransferDetailsStatusInfo.vue';

const props = defineProps({
  transfer: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  isLoading: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const authStore = useAuthStore();

const isAgencyUser = computed(() => authStore.isAgency);

const senderBeneficiaryLabel = computed(() => (isAgencyUser.value ? 'Sender' : 'Beneficiary'));

const senderBusinessName = computed(() => {
  return props.transfer.sender?.business_name;
});

const recipientBusinessName = computed(() => {
  return props.transfer.recipient?.business_name;
});

const transferCurrencyCode = computed(() => {
  return isAgencyUser.value ? props.transfer.target_currency_alpha_code : props.transfer.source_currency_alpha_code;
});

const totalCommission = computed(() => {
  return formatNumber({
    value: props.transfer.total_commissionable_fare,
    options: { style: 'currency', currency: transferCurrencyCode.value || 'USD' },
  });
});

const cardBorderColor = computed(() => {
  return (
    {
      [TransferStatus.FAILED_VERIFICATION]: 'warning',
      [TransferStatus.CANCELED]: 'error',
    }[props.transfer.status] || 'neutral'
  );
});

const invoiceProvider = computed(() => props.transfer?.recipient?.invoice_provider);
const invoicingDisabled = computed(
  () =>
    invoiceProvider.value !== InvoiceProviders.SION &&
    [TransferStatus.RELEASED, TransferStatus.SETTLED, TransferStatus.CANCELED].includes(props.transfer?.status)
);

const router = useRouter();
const { closeDrawer } = useTransferDrawerStore();

const handleSeeDetails = () => {
  router.push({
    name: 'transfers-details',
    params: { transferId: props.transfer.id },
    state: { from: 'statements' },
  });
  closeDrawer();
};
</script>
